// FaturamentoCompleto.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import api from "../../api/api";
import { useTheme } from "../../context/ThemeContext";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import RelatorioFaturamentoPDF from "./RelatorioFaturamentoPDF";
import DespesasList from "../../components/despesas/DespesasList";
import "./Faturamento.scss";

const formatCurrency = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
const formatPercent = (v) =>
  v != null && !isNaN(v) ? `${v.toFixed(1)}%` : "0.0%";

const SkeletonCard = () => (
  <div className="stat-card stat-card--skeleton">
    <div className="skeleton skeleton--value" />
  </div>
);

const StatCard = ({ title, value, icon, accent, tooltip }) => (
  <div
    className={`stat-card${accent ? " stat-card--accent" : ""}`}
    title={tooltip}
  >
    <div className="stat-card__icon">{icon}</div>
    <div className="stat-card__body">
      <span className="stat-card__label">{title}</span>
      <span className="stat-card__value">{value}</span>
    </div>
  </div>
);

const FaturamentoCompleto = () => {
  const { isDark } = useTheme();
  const [periodo, setPeriodo] = useState({ dataInicio: "", dataFim: "" });
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detalhes, setDetalhes] = useState([]);
  const [despesasPorCategoria, setDespesasPorCategoria] = useState([]);
  const [evolucao, setEvolucao] = useState([]);
  const [empresaNome, setEmpresaNome] = useState("");
  const [refreshDespesasFlag, setRefreshDespesasFlag] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (periodo.dataInicio) params.dataInicio = periodo.dataInicio;
      if (periodo.dataFim) params.dataFim = periodo.dataFim;

      const [resumoRes, evolucaoRes, detalhesRes, despesasCatRes] =
        await Promise.all([
          api.get("/faturamento/completo", { params }),
          api.get("/faturamento/evolucao", {
            params: { ...params, agrupamento: "month" },
          }),
          api.get("/faturamento/detalhes", {
            params: { ...params, limite: 200 },
          }),
          api.get("/faturamento/despesas-por-categoria", { params }),
        ]);

      setResumo(resumoRes.data);
      setEvolucao(evolucaoRes.data);
      setDetalhes(detalhesRes.data);
      setDespesasPorCategoria(despesasCatRes.data);

      try {
        const clienteRes = await api.get("/clientes");
        setEmpresaNome(clienteRes.data.nome);
      } catch {
        setEmpresaNome("Minha Empresa");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshDespesasFlag]);

  const handleDespesaChange = () => setRefreshDespesasFlag((prev) => prev + 1);

  const chartColors = useMemo(() => {
    const getVar = (name) =>
      getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return {
      primary:  getVar("--color-primary")  || "#1A56DB",
      success:  getVar("--color-success")  || "#10B981",
      warning:  "#F59E0B",
      danger:   "#EF4444",
      border:   getVar("--color-border")   || "#E5E7EB",
      textMuted: getVar("--color-text-muted") || "#6B7280",
      surface:  getVar("--color-surface")  || "#FFFFFF",
    };
  }, [isDark]);

  const exportToExcel = () => {
    if (!resumo) return;
    const wsData = [
      ["RELATÓRIO DE FATURAMENTO"],
      [`Período: ${periodo.dataInicio || "início"} até ${periodo.dataFim || "hoje"}`],
      [],
      ["Indicador", "Valor"],
      ["Receita Bruta", resumo.receita_total],
      ["Custo dos Produtos", resumo.custo_produtos],
      ["Despesas Operacionais", resumo.despesas_operacionais],
      ["Lucro Bruto", resumo.lucro_bruto],
      ["Lucro Líquido", resumo.lucro_liquido],
      ["Margem Bruta", `${resumo.margem_bruta.toFixed(1)}%`],
      ["Margem Líquida", `${resumo.margem_liquida.toFixed(1)}%`],
      [],
      ["DESPESAS POR CATEGORIA"],
      ["Categoria", "Valor"],
      ...despesasPorCategoria.map((d) => [d.categoria, d.total]),
      [],
      ["DETALHAMENTO DE TRANSAÇÕES"],
      ["Data", "Tipo", "Descrição", "Receita", "Custo", "Despesa", "Lucro"],
      ...detalhes.map((item) => [
        new Date(item.data).toLocaleDateString("pt-BR"),
        item.tipo,
        item.descricao,
        item.receita || 0,
        item.custo || 0,
        item.despesa || 0,
        (item.receita || 0) - (item.custo || 0) - (item.despesa || 0),
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Faturamento");
    XLSX.writeFile(wb, `faturamento_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const generatePDF = async () => {
    if (!resumo) return;
    const blob = await pdf(
      <RelatorioFaturamentoPDF
        resumo={resumo}
        evolucao={evolucao}
        despesasPorCategoria={despesasPorCategoria}
        periodo={periodo}
        empresa={empresaNome}
      />
    ).toBlob();
    saveAs(blob, `relatorio_faturamento_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const tooltipStyle = {
    background: chartColors.surface,
    color: chartColors.textMuted,
    border: `1px solid ${chartColors.border}`,
    borderRadius: "8px",
    fontSize: "12px",
    padding: "6px 10px",
  };

  return (
    <div className="faturamento">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <header className="faturamento__header">
        <h1>📈 Faturamento Completo</h1>
        <div className="faturamento__filtros">
          <input
            type="date"
            value={periodo.dataInicio}
            onChange={(e) => setPeriodo((p) => ({ ...p, dataInicio: e.target.value }))}
          />
          <input
            type="date"
            value={periodo.dataFim}
            onChange={(e) => setPeriodo((p) => ({ ...p, dataFim: e.target.value }))}
          />
          <button className="btn-add" onClick={fetchData}>
            Aplicar
          </button>
          <button className="btn-edit" onClick={exportToExcel} disabled={loading}>
            📊 Excel
          </button>
          <button className="btn-save" onClick={generatePDF} disabled={loading}>
            📄 PDF
          </button>
        </div>
      </header>

      {/* ── KPIs ───────────────────────────────────────────────────── */}
      <div className="stats-grid">
        {loading ? (
          Array(6).fill(null).map((_, i) => <SkeletonCard key={i} />)
        ) : (
          <>
            <StatCard
              title="Receita Bruta"
              value={formatCurrency(resumo?.receita_total)}
              icon="💰"
            />
            <StatCard
              title="CMV (Custo Produtos)"
              value={formatCurrency(resumo?.custo_produtos)}
              icon="🏭"
              accent
            />
            <StatCard
              title="Despesas Operacionais"
              value={formatCurrency(resumo?.despesas_operacionais)}
              icon="📉"
              accent
            />
            <StatCard
              title="Lucro Bruto"
              value={formatCurrency(resumo?.lucro_bruto)}
              icon="📈"
            />
            <StatCard
              title="Lucro Líquido"
              value={formatCurrency(resumo?.lucro_liquido)}
              icon="⭐"
              accent
            />
            <div className="stat-card stat-card--double">
              <div className="stat-card__double-item">
                <span className="stat-card__label">Margem Bruta</span>
                <span className="stat-card__value">
                  {formatPercent(resumo?.margem_bruta)}
                </span>
              </div>
              <div className="stat-card__double-item">
                <span className="stat-card__label">Margem Líquida</span>
                <span className="stat-card__value">
                  {formatPercent(resumo?.margem_liquida)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Gráfico de evolução ─────────────────────────────────────── */}
      <div className="chart-evolucao">
        <h3>Evolução Mensal</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart
            data={evolucao}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="gradReceita" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={chartColors.success} stopOpacity={0.2} />
                <stop offset="95%" stopColor={chartColors.success} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradCusto" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={chartColors.warning} stopOpacity={0.2} />
                <stop offset="95%" stopColor={chartColors.warning} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradLucro" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={chartColors.primary} stopOpacity={0.2} />
                <stop offset="95%" stopColor={chartColors.primary} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={chartColors.border} />
            <XAxis dataKey="periodo" stroke={chartColors.textMuted} tick={{ fontSize: 11 }} />
            <YAxis
              tickFormatter={(v) => `R$${v / 1000}k`}
              stroke={chartColors.textMuted}
              tick={{ fontSize: 11 }}
              width={60}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(value) => formatCurrency(value)}
            />
            <Legend wrapperStyle={{ fontSize: "12px" }} />
            <Area
              type="monotone"
              dataKey="receita"
              name="Receita"
              stroke={chartColors.success}
              fill="url(#gradReceita)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="custo"
              name="Custo"
              stroke={chartColors.warning}
              fill="url(#gradCusto)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="lucro"
              name="Lucro"
              stroke={chartColors.primary}
              fill="url(#gradLucro)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* ── Despesas por categoria ──────────────────────────────────── */}
      <div className="despesas-categorias">
        <h3>Despesas por Categoria</h3>
        <div className="despesas-grid">
          {despesasPorCategoria.map((cat) => (
            <div key={cat.categoria} className="categoria-card">
              <span className="categoria-nome">{cat.categoria}</span>
              <span className="categoria-valor">{formatCurrency(cat.total)}</span>
            </div>
          ))}
          {despesasPorCategoria.length === 0 && !loading && (
            <p style={{ color: "var(--color-text-muted)", fontSize: "0.875rem" }}>
              Nenhuma despesa no período.
            </p>
          )}
        </div>
      </div>

      {/* ── Gerenciar despesas ──────────────────────────────────────── */}
      <div className="despesas-list-section">
        <h3>Gerenciar Despesas</h3>
        <DespesasList onDespesaChange={handleDespesaChange} />
      </div>

      {/* ── Tabela de lançamentos ───────────────────────────────────── */}
      <div className="tabela-detalhes">
        <h3>Lançamentos (Receitas, Custos e Despesas)</h3>
        <div className="table-wrapper">
          <table className="faturamento-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Tipo</th>
                <th>Descrição</th>
                <th>Receita</th>
                <th>Custo</th>
                <th>Despesa</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {detalhes.map((item) => {
                const resultado =
                  (item.receita || 0) - (item.custo || 0) - (item.despesa || 0);
                return (
                  <tr key={item.id}>
                    <td data-label="Data">
                      {new Date(item.data).toLocaleDateString("pt-BR")}
                    </td>
                    <td data-label="Tipo">
                      <span className={`badge badge--${item.tipo}`}>
                        {item.tipo}
                      </span>
                    </td>
                    <td data-label="Descrição">{item.descricao}</td>
                    <td className="valor positivo" data-label="Receita">
                      {formatCurrency(item.receita)}
                    </td>
                    <td className="valor negativo" data-label="Custo">
                      {formatCurrency(item.custo)}
                    </td>
                    <td className="valor negativo" data-label="Despesa">
                      {formatCurrency(item.despesa)}
                    </td>
                    <td className="valor lucro" data-label="Resultado">
                      {formatCurrency(resultado)}
                    </td>
                  </tr>
                );
              })}
              {detalhes.length === 0 && !loading && (
                <tr>
                  <td colSpan="7" className="empty-message">
                    Nenhum lançamento encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default FaturamentoCompleto;